import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestService } from '../services/supabaseClient';
import '../styles/PartnerRequests.css';

// ============================================================
// DB CONNECTED
// ------------------------------------------------------------
// Was previously reading/writing a local `wiiz_test_enrolments` list
// in localStorage (testing-mode placeholder from before Supabase was
// wired up) — that's why requests created via the real registration
// flow never showed up here. Now pulls the partner's actual `requests`
// rows via requestService.getForPartner, already joined with student
// + course details.
//
// requests.status only allows: 'registered' | 'pending' | 'completed'
// | 'cancelled' (DB check constraint). 'pending' is what "My Students"
// sets when the partner clicks 🎓 Request Certification, so that's
// the state this page treats as "certificate requested, awaiting
// admin action" — same mapping used in PartnerStudent.jsx.
// ============================================================

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Pill({ value }) {
  if (!value) return <span className="pill pill-registered">—</span>;
  const cls = value.toLowerCase();
  return <span className={`pill pill-${cls}`}>{value.replace('_', ' ')}</span>;
}

export default function PartnerRequests({ session }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('ALL');
  const [paymentFilter, setPaymentFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [personFilter, setPersonFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await requestService.getForPartner(session.partnerId);

      // Only rows this partner actually requested certification for
      // belong here — plain 'registered' rows (nobody clicked 🎓 yet)
      // live on My Students only.
      setRows((data || []).filter(row => row.status !== 'registered'));
    } catch (err) {
      setError(err.message || 'Unable to load your requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.partnerId) load();
  }, [session?.partnerId]);

  const personName = row => `${row.students?.first_name || ''} ${row.students?.last_name || ''}`.trim();

  // Unique dropdown options built from whatever's actually in the
  // data, so the filters never drift out of sync with real values
  // (course "type" and payment_status strings live in the DB, not
  // hardcoded here).
  const personOptions = useMemo(() => {
    const names = new Set(rows.map(personName).filter(Boolean));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const kindOptions = useMemo(() => {
    const kinds = new Set(rows.map(row => row.course?.type).filter(Boolean));
    return Array.from(kinds).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const paymentOptions = useMemo(() => {
    const payments = new Set(rows.map(row => row.payment_status).filter(Boolean));
    return Array.from(payments).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999); // include the whole "to" day

    return rows.filter(row => {
      if (query) {
        const id = (row.enrollment_id || '').toLowerCase();
        const name = personName(row).toLowerCase();
        const email = (row.students?.email || '').toLowerCase();
        const content = (row.course?.name || '').toLowerCase();
        const matchesSearch = id.includes(query) || name.includes(query) || email.includes(query) || content.includes(query);
        if (!matchesSearch) return false;
      }

      if (kindFilter !== 'ALL' && row.course?.type !== kindFilter) return false;
      if (paymentFilter !== 'ALL' && row.payment_status !== paymentFilter) return false;
      if (statusFilter !== 'ALL' && row.status !== statusFilter) return false;
      if (personFilter !== 'ALL' && personName(row) !== personFilter) return false;

      if (from || to) {
        const created = row.created_at ? new Date(row.created_at) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }

      return true;
    });
  }, [rows, search, kindFilter, paymentFilter, statusFilter, personFilter, dateFrom, dateTo]);

  const hasActiveFilters =
    search || kindFilter !== 'ALL' || paymentFilter !== 'ALL' || statusFilter !== 'ALL' || personFilter !== 'ALL' || dateFrom || dateTo;

  const clearFilters = () => {
    setSearch('');
    setKindFilter('ALL');
    setPaymentFilter('ALL');
    setStatusFilter('ALL');
    setPersonFilter('ALL');
    setDateFrom('');
    setDateTo('');
  };

  const renderAction = row => {
    if (row.status === 'completed') {
      return (
        <button
          type="button"
          className="action-btn qr"
          onClick={() => navigate('/academy/partner/certificates', { state: { enrollmentId: row.enrollment_id } })}
        >
          View Certificate
        </button>
      );
    }

    if (row.status === 'pending') {
      return (
        <button type="button" className="action-btn" disabled>
          Awaiting Admin
        </button>
      );
    }

    if (row.status === 'cancelled') {
      return (
        <button type="button" className="action-btn" disabled>
          Cancelled
        </button>
      );
    }

    return (
      <button type="button" className="action-btn" disabled>
        —
      </button>
    );
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <span className="eyebrow">Requests</span>
          <h2>Your Requests</h2>
          <p>Track registration, payment and credential state.</p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filter-field">
          <label htmlFor="filter-kind">Kind</label>
          <select id="filter-kind" value={kindFilter} onChange={event => setKindFilter(event.target.value)}>
            <option value="ALL">All Kinds</option>
            {kindOptions.map(kind => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-payment">Payment</label>
          <select id="filter-payment" value={paymentFilter} onChange={event => setPaymentFilter(event.target.value)}>
            <option value="ALL">All Payments</option>
            {paymentOptions.map(payment => (
              <option key={payment} value={payment}>
                {payment}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-status">Status</label>
          <select id="filter-status" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
            <option value="ALL">All Statuses</option>
            <option value="registered">Registered</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-person">Person</label>
          <select id="filter-person" value={personFilter} onChange={event => setPersonFilter(event.target.value)}>
            <option value="ALL">All People</option>
            {personOptions.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-date-from">From</label>
          <input
            id="filter-date-from"
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={event => setDateFrom(event.target.value)}
          />
        </div>

        <div className="filter-field">
          <label htmlFor="filter-date-to">To</label>
          <input
            id="filter-date-to"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={event => setDateTo(event.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button type="button" className="secondary clear-filters" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="table-wrapper">
        <table className="requests-table">
          <thead>
            <tr>
              <th>Enrolment</th>
              <th>Student ID</th>
              <th>Person</th>
              <th>Content</th>
              <th>Kind</th>
              <th>Payment</th>
              <th>Status</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="empty-state">
                  Loading requests...
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="empty-state">
                  {hasActiveFilters ? 'No requests match your filters.' : 'No requests yet.'}
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map(row => (
                <tr key={row.enrollment_id}>
                  <td className="enrolment-id-cell">{row.enrollment_id}</td>
                  <td className="student-id-cell">{row.students?.student_id || '—'}</td>
                  <td className="person-cell">
                    <b>{personName(row)}</b>
                    <small>{row.students?.email}</small>
                  </td>
                  <td>{row.course?.name || '—'}</td>
                  <td>
                    <Pill value={row.course?.type} />
                  </td>
                  <td>
                    <Pill value={row.payment_status} />
                  </td>
                  <td>
                    <Pill value={row.status} />
                  </td>
                  <td>{formatDate(row.created_at)}</td>
                  <td className="action-cell">{renderAction(row)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}